import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Post } from "../../../utils/apiService";
import {
  AddUpdateEntryStickerForParkingBody,
  AddUpdateIssuanceRenewalIdForStaffFromContractedCompaniesBody,
  AddUpdateLostStickerRequestBody,
  AddUpdateOfficeAccessAuthorizationBody,
  AddUpdatePCPermitBody,
  AddUpdatePermitToWorkBody,
  AddUpdateWarehouseInOutPermitBody,
  ApproveIssuanceRenewalIdForStaffFromContractedCompaniesBody,
  ApproveLostStickerIDCardRequestBody,
  ApproveOfficeAccessAuthorizationBody,
  ApproveParkingEntryStickerBody,
  ApprovePcInOutPermitBody,
  ApprovePermitToWorkBody,
  ApproveWarehouseInOutPermitBody,
  CancelIssuanceRenewalIdForStaffFromContractedCompaniesBody,
  CancelLostStickerIDCardRequestBody,
  CancelOfficeAccessAuthorizationBody,
  CancelParkingEntryStickerBody,
  CancelPermitByIdBody,
  DeleteAttachmentBody,
  GetPermitDetailsByIDBody,
  RejectIssuanceRenewalIdForStaffFromContractedCompaniesBody,
  RejectLostStickerIDCardRequestBody,
  RejectOfficeAccessAuthorizationBody,
  RejectParkingEntryStickerBody,
  RejectPcInOutPermitBody,
  RejectPermitToWorkBody,
  RejectWarehouseInOutPermitBody,
} from "../interface";
import { GetFileByTypeBody } from "../../visitor/interface";
import { FETCH_PERMIT_DETAILS_KEYS } from "../query";

const sendFinalPermitEmailAction = async (body: FormData) => {
  return Post({
    url: "/api/Permits/SendApprovedPermitEmail",
    body,
    isFormData: true,
  });
};

export const useSendFinalPermitEmail = () => {
  return useMutation({
    mutationFn: async (body: FormData) =>
      sendFinalPermitEmailAction(body),
  });
};

const getPermitToWorkDetailsByIDAction = async (body: GetPermitDetailsByIDBody) => {
  return Post({
    url: "/api/PermitToWork/GetPermitDetailsByID",
    body,
  });
};

export const useGetPermitToWorkDetailsByID = () => {
  return useMutation({
    mutationFn: async (body: GetPermitDetailsByIDBody) =>
      getPermitToWorkDetailsByIDAction(body),
  });
};

const getWarehouseInOutPermitDetailsByIDAction = async (body: GetPermitDetailsByIDBody) => {
  return Post({
    url: "/api/PermitWarehouse/GetPermitDetailsByID",
    body,
  });
};

export const useGetWarehouseInOutPermitDetailsByID = () => {
  return useMutation({
    mutationFn: async (body: GetPermitDetailsByIDBody) =>
      getWarehouseInOutPermitDetailsByIDAction(body),
  });
};

const getPcInOutPermitDetailsByIDAction = async (body: GetPermitDetailsByIDBody) => {
  return Post({
    url: "/api/PermitLaptop/GetPermitDetailsByID",
    body,
  });
};

export const useGetPcInOutPermitDetailsByID = () => {
  return useMutation({
    mutationFn: async (body: GetPermitDetailsByIDBody) =>
      getPcInOutPermitDetailsByIDAction(body),
  });
};

const getLostStickerIDCardRequestPermitDetailsByIDAction = async (body: GetPermitDetailsByIDBody) => {
  return Post({
    url: "/api/LostStickerRequest/GetPermitDetailsByID",
    body,
  });
};

export const useGetLostStickerIDCardRequestPermitDetailsByID = () => {
  return useMutation({
    mutationFn: async (body: GetPermitDetailsByIDBody) =>
      getLostStickerIDCardRequestPermitDetailsByIDAction(body),
  });
};

const getParkingStickerRequestPermitDetailsByIDAction = async (body: GetPermitDetailsByIDBody) => {
  return Post({
    url: "/api/ParkingEntrySticker/GetPermitDetailsByID",
    body,
  });
};

export const useGetParkingStickerRequestPermitDetailsByID = () => {
  return useMutation({
    mutationFn: async (body: GetPermitDetailsByIDBody) =>
      getParkingStickerRequestPermitDetailsByIDAction(body),
  });
};

const getIssuanceRenewalIdForStaffFromContractedCompaniesPermitDetailsByIDAction = async (body: GetPermitDetailsByIDBody) => {
  return Post({
    url: "/api/ContractorStaffId/GetPermitDetailsByID",
    body,
  });
};

export const useGetIssuanceRenewalIdForStaffFromContractedCompaniesPermitDetailsByID = () => {
  return useMutation({
    mutationFn: async (body: GetPermitDetailsByIDBody) =>
      getIssuanceRenewalIdForStaffFromContractedCompaniesPermitDetailsByIDAction(body),
  });
};

const getOfficeAccessAuthorizationPermitDetailsByIDAction = async (body: GetPermitDetailsByIDBody) => {
  return Post({
    url: "/api/OfficeAccessAuthorisation/GetPermitDetailsByID",
    body,
  });
};

export const useGetOfficeAccessAuthorizationPermitDetailsByID = () => {
  return useMutation({
    mutationFn: async (body: GetPermitDetailsByIDBody) =>
      getOfficeAccessAuthorizationPermitDetailsByIDAction(body),
  });
};

const addUpdatePermitToWorkAction = async (body: AddUpdatePermitToWorkBody) => {
  return Post({
    url: "/api/PermitToWork/AddUpdate",
    body,
  });
};

export const useAddUpdatePermitToWork = () => {
  return useMutation({
    mutationFn: async (body: AddUpdatePermitToWorkBody) =>
      addUpdatePermitToWorkAction(body),
  });
};

const addUpdateOfficeAccessAuthorizationAction = async (body: AddUpdateOfficeAccessAuthorizationBody) => {
  return Post({
    url: "/api/OfficeAccessAuthorisation/AddUpdate",
    body,
  });
};

export const useAddUpdateOfficeAccessAuthorization = () => {
  return useMutation({
    mutationFn: async (body: AddUpdateOfficeAccessAuthorizationBody) =>
      addUpdateOfficeAccessAuthorizationAction(body),
  });
};

const addUpdateIssuanceRenewalIdForStaffFromContractedCompaniesAction = async (body: AddUpdateIssuanceRenewalIdForStaffFromContractedCompaniesBody) => {
  return Post({
    url: "/api/ContractorStaffId/AddUpdate",
    body,
  });
};

export const useAddUpdateIssuanceRenewalIdForStaffFromContractedCompanies = () => {
  return useMutation({
    mutationFn: async (body: AddUpdateIssuanceRenewalIdForStaffFromContractedCompaniesBody) =>
      addUpdateIssuanceRenewalIdForStaffFromContractedCompaniesAction(body),
  });
};

const addUpdateEntryStickerForParkingAction = async (body: AddUpdateEntryStickerForParkingBody) => {
  return Post({
    url: "/api/ParkingEntrySticker/AddUpdate",
    body,
  });
};

export const useAddUpdateEntryStickerForParking = () => {
  return useMutation({
    mutationFn: async (body: AddUpdateEntryStickerForParkingBody) =>
      addUpdateEntryStickerForParkingAction(body),
  });
};

const cancelPcInOutPermitByIdAction = async (body: CancelPermitByIdBody) => {
  return Post({
    url: "/api/PermitLaptop/Cancel",
    body,
  });
};

export const useCancelPcInOutPermitById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CancelPermitByIdBody) =>
      cancelPcInOutPermitByIdAction(body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [FETCH_PERMIT_DETAILS_KEYS.FETCH_ALL_SEARCH_PERMIT_REQUESTS],
      }),
  });
};

const cancelWarehouseInOutPermitByIdAction = async (
  body: CancelPermitByIdBody,
) => {
  return Post({
    url: "/api/PermitWarehouse/Cancel",
    body,
  });
};

export const useCancelWarehouseInOutPermitById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CancelPermitByIdBody) =>
      cancelWarehouseInOutPermitByIdAction(body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [FETCH_PERMIT_DETAILS_KEYS.FETCH_ALL_SEARCH_PERMIT_REQUESTS],
      }),
  });
};

const rejectPcInOutPermitAction = async (body: RejectPcInOutPermitBody) => {
  return Post({
    url: "/api/PermitLaptop/Reject",
    body,
  });
};

export const useRejectPcInOutPermit = () => {
  return useMutation({
    mutationFn: async (body: RejectPcInOutPermitBody) =>
      rejectPcInOutPermitAction(body),
  });
};

const approvePcInOutPermitAction = async (body: ApprovePcInOutPermitBody) => {
  return Post({
    url: "/api/PermitLaptop/Approve",
    body,
  });
};

export const useApprovePcInOutPermit = () => {
  return useMutation({
    mutationFn: async (body: ApprovePcInOutPermitBody) =>
      approvePcInOutPermitAction(body),
  });
};

const addUpdatePCPermitAction = async (body: AddUpdatePCPermitBody) => {
  return Post({
    url: "/api/PermitLaptop/AddUpdate",
    body,
  });
};

export const useAddUpdatePCPermit = () => {
  return useMutation({
    mutationFn: async (body: AddUpdatePCPermitBody) =>
      addUpdatePCPermitAction(body),
  });
};

const addUpdateLostStickerRequestAction = async (body: AddUpdateLostStickerRequestBody) => {
  return Post({
    url: "/api/LostStickerRequest/AddUpdate",
    body,
  });
};

export const useAddUpdateLostStickerRequest = () => {
  return useMutation({
    mutationFn: async (body: AddUpdateLostStickerRequestBody) =>
      addUpdateLostStickerRequestAction(body),
  });
};

const getPermitToWorkFileByTypeAction = async (body: GetFileByTypeBody) => {
  return Post({
    url: "/api/PermitToWork/GetFile",
    body,
  });
};

export const useGetPermitToWorkFileByType = () => {
  return useMutation({
    mutationFn: async (body: GetFileByTypeBody) =>
      getPermitToWorkFileByTypeAction(body),
  });
};

const getPCPermitFileByTypeAction = async (body: GetFileByTypeBody) => {
  return Post({
    url: "/api/PermitLaptop/GetFile",
    body,
  });
};

export const useGetPCPermitFileByType = () => {
  return useMutation({
    mutationFn: async (body: GetFileByTypeBody) =>
      getPCPermitFileByTypeAction(body),
  });
};

const addUpdateWarehouseInOutPermitAction = async (
  body: AddUpdateWarehouseInOutPermitBody,
) => {
  return Post({
    url: "/api/PermitWarehouse/AddUpdate",
    body,
  });
};

export const useAddUpdateWarehouseInOutPermit = () => {
  return useMutation({
    mutationFn: async (body: AddUpdateWarehouseInOutPermitBody) =>
      addUpdateWarehouseInOutPermitAction(body),
  });
};

const getWarehouseInOutPermitFileByTypeAction = async (
  body: GetFileByTypeBody,
) => {
  return Post({
    url: "/api/PermitWarehouse/GetFile",
    body,
  });
};

export const useGetWarehouseInOutPermitFileByType = () => {
  return useMutation({
    mutationFn: async (body: GetFileByTypeBody) =>
      getWarehouseInOutPermitFileByTypeAction(body),
  });
};

const getParkingEntryStickerFileByTypeAction = async (
  body: GetFileByTypeBody,
) => {
  return Post({
    url: "/api/ParkingEntrySticker/GetFile",
    body,
  });
};

export const useGetParkingEntryStickerFileByType = () => {
  return useMutation({
    mutationFn: async (body: GetFileByTypeBody) =>
      getParkingEntryStickerFileByTypeAction(body),
  });
};

const getIssuanceRenewalIdForStaffFromContractedCompaniesFileByTypeAction = async (
  body: GetFileByTypeBody,
) => {
  return Post({
    url: "/api/ContractorStaffId/GetFile",
    body,
  });
};

export const useGetIssuanceRenewalIdForStaffFromContractedCompaniesFileByType = () => {
  return useMutation({
    mutationFn: async (body: GetFileByTypeBody) =>
      getIssuanceRenewalIdForStaffFromContractedCompaniesFileByTypeAction(body),
  });
};

const approveWarehouseInOutPermitAction = async (
  body: ApproveWarehouseInOutPermitBody,
) => {
  return Post({
    url: "/api/PermitWarehouse/Approve",
    body,
  });
};

export const useApproveWarehouseInOutPermit = () => {
  return useMutation({
    mutationFn: async (body: ApproveWarehouseInOutPermitBody) =>
      approveWarehouseInOutPermitAction(body),
  });
};

const rejectWarehouseInOutPermitAction = async (
  body: RejectWarehouseInOutPermitBody,
) => {
  return Post({
    url: "/api/PermitWarehouse/Reject",
    body,
  });
};

export const useRejectWarehouseInOutPermit = () => {
  return useMutation({
    mutationFn: async (body: RejectWarehouseInOutPermitBody) =>
      rejectWarehouseInOutPermitAction(body),
  });
};

const approvePermitToWorkAction = async (body: ApprovePermitToWorkBody) => {
  return Post({
    url: "/api/PermitToWork/Approve",
    body,
  });
};

export const useApprovePermitToWork = () => {
  return useMutation({
    mutationFn: async (body: ApprovePermitToWorkBody) =>
      approvePermitToWorkAction(body),
  });
};

const approveLostStickerIDCardRequestAction = async (body: ApproveLostStickerIDCardRequestBody) => {
  return Post({
    url: "/api/LostStickerRequest/Approve",
    body,
  });
};

export const useApproveLostStickerIDCardRequest = () => {
  return useMutation({
    mutationFn: async (body: ApproveLostStickerIDCardRequestBody) =>
      approveLostStickerIDCardRequestAction(body),
  });
};

const approveParkingEntryStickerAction = async (body: ApproveParkingEntryStickerBody) => {
  return Post({
    url: "/api/ParkingEntrySticker/Approve",
    body,
  });
};

export const useApproveParkingEntrySticker = () => {
  return useMutation({
    mutationFn: async (body: ApproveParkingEntryStickerBody) =>
      approveParkingEntryStickerAction(body),
  });
};

const approveOfficeAccessAuthorizationAction = async (body: ApproveOfficeAccessAuthorizationBody) => {
  return Post({
    url: "/api/OfficeAccessAuthorisation/Approve",
    body,
  });
};

export const useApproveOfficeAccessAuthorization = () => {
  return useMutation({
    mutationFn: async (body: ApproveOfficeAccessAuthorizationBody) =>
      approveOfficeAccessAuthorizationAction(body),
  });
};

// 
const approveIssuanceRenewalIdForStaffFromContractedCompaniesAction = async (body: ApproveIssuanceRenewalIdForStaffFromContractedCompaniesBody) => {
  return Post({
    url: "/api/ContractorStaffId/Approve",
    body,
  });
};

export const useApproveIssuanceRenewalIdForStaffFromContractedCompanies = () => {
  return useMutation({
    mutationFn: async (body: ApproveIssuanceRenewalIdForStaffFromContractedCompaniesBody) =>
      approveIssuanceRenewalIdForStaffFromContractedCompaniesAction(body),
  });
};
// 

const rejectPermitToWorkAction = async (body: RejectPermitToWorkBody) => {
  return Post({
    url: "/api/PermitToWork/Reject",
    body,
  });
};

export const useRejectPermitToWork = () => {
  return useMutation({
    mutationFn: async (body: RejectPermitToWorkBody) =>
      rejectPermitToWorkAction(body),
  });
};

const rejectLostStickerIDCardRequestAction = async (body: RejectLostStickerIDCardRequestBody) => {
  return Post({
    url: "/api/LostStickerRequest/Reject",
    body,
  });
};

export const useRejectLostStickerIDCardRequest = () => {
  return useMutation({
    mutationFn: async (body: RejectLostStickerIDCardRequestBody) =>
      rejectLostStickerIDCardRequestAction(body),
  });
};

const rejectParkingEntryStickerAction = async (body: RejectParkingEntryStickerBody) => {
  return Post({
    url: "/api/ParkingEntrySticker/Reject",
    body,
  });
};

export const useRejectParkingEntrySticker = () => {
  return useMutation({
    mutationFn: async (body: RejectParkingEntryStickerBody) =>
      rejectParkingEntryStickerAction(body),
  });
};

const rejectOfficeAccessAuthorizationAction = async (body: RejectOfficeAccessAuthorizationBody) => {
  return Post({
    url: "/api/OfficeAccessAuthorisation/Reject",
    body,
  });
};

export const useRejectOfficeAccessAuthorization = () => {
  return useMutation({
    mutationFn: async (body: RejectOfficeAccessAuthorizationBody) =>
      rejectOfficeAccessAuthorizationAction(body),
  });
};

const rejectIssuanceRenewalIdForStaffFromContractedCompaniesAction = async (body: RejectIssuanceRenewalIdForStaffFromContractedCompaniesBody) => {
  return Post({
    url: "/api/ContractorStaffId/Reject",
    body,
  });
};

export const useRejectIssuanceRenewalIdForStaffFromContractedCompanies = () => {
  return useMutation({
    mutationFn: async (body: RejectIssuanceRenewalIdForStaffFromContractedCompaniesBody) =>
      rejectIssuanceRenewalIdForStaffFromContractedCompaniesAction(body),
  });
};

const cancelPermitToWorkByIdAction = async (
  body: CancelPermitByIdBody,
) => {
  return Post({
    url: "/api/PermitToWork/Cancel",
    body,
  });
};

export const useCancelPermitToWorkById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CancelPermitByIdBody) =>
      cancelPermitToWorkByIdAction(body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [FETCH_PERMIT_DETAILS_KEYS.FETCH_ALL_SEARCH_PERMIT_REQUESTS],
      }),
  });
};

const cancelLostStickerIDCardRequestAction = async (
  body: CancelLostStickerIDCardRequestBody,
) => {
  return Post({
    url: "/api/LostStickerRequest/Cancel",
    body,
  });
};

export const useCancelLostStickerIDCardRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CancelLostStickerIDCardRequestBody) =>
      cancelLostStickerIDCardRequestAction(body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [FETCH_PERMIT_DETAILS_KEYS.FETCH_ALL_SEARCH_PERMIT_REQUESTS],
      }),
  });
};

const cancelParkingEntryStickerAction = async (
  body: CancelParkingEntryStickerBody,
) => {
  return Post({
    url: "/api/ParkingEntrySticker/Cancel",
    body,
  });
};

export const useCancelParkingEntrySticker = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CancelParkingEntryStickerBody) =>
      cancelParkingEntryStickerAction(body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [FETCH_PERMIT_DETAILS_KEYS.FETCH_ALL_SEARCH_PERMIT_REQUESTS],
      }),
  });
};

const cancelOfficeAccessAuthorizationAction = async (
  body: CancelOfficeAccessAuthorizationBody,
) => {
  return Post({
    url: "/api/OfficeAccessAuthorisation/Cancel",
    body,
  });
};

export const useCancelOfficeAccessAuthorization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CancelOfficeAccessAuthorizationBody) =>
      cancelOfficeAccessAuthorizationAction(body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [FETCH_PERMIT_DETAILS_KEYS.FETCH_ALL_SEARCH_PERMIT_REQUESTS],
      }),
  });
};

// 
const deletePermitToWorkAttachmentAction = async (body: DeleteAttachmentBody) => {
  return Post({
    url: "/api/PermitToWork/DeleteFile",
    body,
  });
};

export const useDeletePermitToWorkAttachment = () => {
  return useMutation({
    mutationFn: async (body: DeleteAttachmentBody) =>
      deletePermitToWorkAttachmentAction(body),
  });
};

const deleteParkingEntryStickerAttachmentAction = async (body: DeleteAttachmentBody) => {
  return Post({
    url: "/api/ParkingEntrySticker/DeleteFile",
    body,
  });
};

export const useDeleteParkingEntryStickerAttachment = () => {
  return useMutation({
    mutationFn: async (body: DeleteAttachmentBody) =>
      deleteParkingEntryStickerAttachmentAction(body),
  });
};

const deleteIssuanceRenewalIdAttachmentAction = async (body: DeleteAttachmentBody) => {
  return Post({
    url: "/api/ContractorStaffId/DeleteFile",
    body,
  });
};

export const useDeleteIssuanceRenewalIdAttachment = () => {
  return useMutation({
    mutationFn: async (body: DeleteAttachmentBody) =>
      deleteIssuanceRenewalIdAttachmentAction(body),
  });
};

const cancelIssuanceRenewalIdForStaffFromContractedCompaniesAction = async (
  body: CancelIssuanceRenewalIdForStaffFromContractedCompaniesBody,
) => {
  return Post({
    url: "/api/ContractorStaffId/Cancel",
    body,
  });
};

export const useCancelIssuanceRenewalIdForStaffFromContractedCompanies = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CancelIssuanceRenewalIdForStaffFromContractedCompaniesBody) =>
      cancelIssuanceRenewalIdForStaffFromContractedCompaniesAction(body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [FETCH_PERMIT_DETAILS_KEYS.FETCH_ALL_SEARCH_PERMIT_REQUESTS],
      }),
  });
};
